import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";

// Public Portal
import PortalLanding from "@/pages/portal/PortalLanding";
import PortalJobs from "@/pages/portal/PortalJobs";
import PortalJobDetail from "@/pages/portal/PortalJobDetail";
import PortalApply from "@/pages/portal/PortalApply";
import PortalTrack from "@/pages/portal/PortalTrack";

// Internal
import Login from "@/pages/Login";
import AppLayout from "@/pages/app/AppLayout";
import Pipeline from "@/pages/app/Pipeline";
import JobsPage from "@/pages/app/JobsPage";
import CandidatesPage from "@/pages/app/CandidatesPage";
import DbsPage from "@/pages/app/DbsPage";
import ReferencesPage from "@/pages/app/ReferencesPage";
import RtwPage from "@/pages/app/RtwPage";
import OnboardingPage from "@/pages/app/OnboardingPage";
import CqcDashboard from "@/pages/app/CqcDashboard";
import ReportsPage from "@/pages/app/ReportsPage";

// Provisioning (PROV-01..17)
import { AdminUsersPage, AdminUserDetailPage, AdminAuditPage, AdminEmailLogPage } from "@/pages/app/AdminPages";
import { InterviewTemplatesPage, AdminSmsLogPage } from "@/pages/app/InterviewTemplatesPage";
import OfferTemplatesPage from "@/pages/app/OfferTemplatesPage";
import EmployeesPage from "@/pages/app/EmployeesPage";
import EmployeeProfilePage from "@/pages/app/EmployeeProfilePage";
import PoliciesPage from "@/pages/app/PoliciesPage";
import LeaversPage from "@/pages/app/LeaversPage";
import PayrollRunPage from "@/pages/app/PayrollRunPage";
import AppraisalsPage from "@/pages/app/AppraisalsPage";
import TrainingPage from "@/pages/app/TrainingPage";
import IntegrationsPage from "@/pages/app/IntegrationsPage";
import BulkSmsPage from "@/pages/app/BulkSmsPage";
import EmployeePortalPage from "@/pages/portal/EmployeePortalPage";
import AdminRemindersPage from "@/pages/app/AdminRemindersPage";
import InterviewDashboardPage from "@/pages/app/InterviewDashboardPage";
import InterviewPackPage from "@/pages/app/InterviewPackPage";
import AdminReferenceRequestsPage from "@/pages/app/AdminReferenceRequestsPage";
import ComplianceDashboardPage from "@/pages/app/ComplianceDashboardPage";
import AdminWipeSampleData from "@/pages/app/AdminWipeSampleData";
import PortalReferenceUpdate from "@/pages/portal/PortalReferenceUpdate";
import RefereePortalPage from "@/pages/portal/RefereePortalPage";
import InterviewConfirm from "@/pages/portal/InterviewConfirm";
import { MfaSetupPage, ChangePasswordPage, OnboardingPortalPage } from "@/pages/auth/SecurityFlows";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--cc-text-soft)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Employees go to their own portal, not the staff ATS.
  if (user.role === "employee") return <Navigate to="/portal/employee" replace />;
  // Forced flows in order: change password → setup MFA → onboarding
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.mfa_required && !user.mfa_enabled) return <Navigate to="/setup-mfa" replace />;
  if (!user.onboarding_completed && !user.onboarding_bypass) return <Navigate to="/onboarding-portal" replace />;
  return children;
}

function AuthOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--cc-text-soft)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) {
    return (
      <div className="p-8 text-center text-[var(--cc-text-soft)]" data-testid="rbac-denied">
        <h1 className="font-display text-2xl font-bold mb-2">Insufficient permissions</h1>
        <p className="text-sm">You do not have access to this page.</p>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Public candidate portal */}
            <Route path="/" element={<PortalLanding />} />
            <Route path="/jobs" element={<PortalJobs />} />
            <Route path="/jobs/:id" element={<PortalJobDetail />} />
            <Route path="/apply/:id" element={<PortalApply />} />
            <Route path="/track" element={<PortalTrack />} />
            <Route path="/interview/confirm/:token" element={<InterviewConfirm />} />
            <Route path="/references/update/:cid" element={<PortalReferenceUpdate />} />
            <Route path="/references/:token/form" element={<RefereePortalPage />} />
            <Route path="/portal/my-policies" element={<AuthOnly><EmployeePortalPage /></AuthOnly>} />
            <Route path="/portal/employee" element={<AuthOnly><EmployeePortalPage /></AuthOnly>} />

            {/* Internal auth flows (need login but bypass main protection) */}
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<AuthOnly><ChangePasswordPage /></AuthOnly>} />
            <Route path="/setup-mfa" element={<AuthOnly><MfaSetupPage /></AuthOnly>} />
            <Route path="/onboarding-portal" element={<AuthOnly><OnboardingPortalPage /></AuthOnly>} />

            {/* Internal protected ATS */}
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Navigate to="/app/pipeline" replace />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="jobs" element={<JobsPage />} />
              <Route path="candidates" element={<CandidatesPage />} />
              <Route path="dbs" element={<DbsPage />} />
              <Route path="references" element={<ReferencesPage />} />
              <Route path="rtw" element={<RtwPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="cqc" element={<CqcDashboard />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="dashboard/interviews" element={<InterviewDashboardPage />} />
              <Route path="dashboard/compliance" element={<ComplianceDashboardPage />} />
              <Route path="candidates/:cid/interviews/:iid/pack" element={<InterviewPackPage />} />
              {/* Admin / Provisioning */}
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route path="admin/users/:id" element={<AdminUserDetailPage />} />
              <Route path="admin/audit" element={<AdminAuditPage />} />
              <Route path="admin/email-log" element={<AdminEmailLogPage />} />
              <Route path="admin/sms-log" element={<AdminSmsLogPage />} />
              <Route path="admin/reminders" element={<AdminRemindersPage />} />
              <Route path="admin/reference-requests" element={<AdminReferenceRequestsPage />} />
              <Route path="admin/interview-templates" element={<InterviewTemplatesPage />} />
              <Route path="admin/offer-templates" element={<OfferTemplatesPage />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="employees/:id" element={<EmployeeProfilePage />} />
              <Route path="admin/policies" element={<PoliciesPage />} />
              <Route path="admin/leavers" element={<RoleGuard roles={["super_admin", "hr_admin"]}><LeaversPage /></RoleGuard>} />
              <Route path="admin/payroll-run" element={<RoleGuard roles={["super_admin", "hr_admin"]}><PayrollRunPage /></RoleGuard>} />
              <Route path="admin/appraisals" element={<AppraisalsPage />} />
              <Route path="admin/training" element={<TrainingPage />} />
              <Route path="admin/integrations" element={<IntegrationsPage />} />
              <Route path="admin/bulk-sms" element={<BulkSmsPage />} />
              <Route path="admin/wipe-sample-data" element={<AdminWipeSampleData />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
